import React, { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@apollo/client";
import { LoadingIndicator } from "../LoadingIndicator";
import * as bundle from "../graphql";
import * as processors from "../processors";
import { GET_GRAPHIC_ELEMENTS_PARAMS, GET_SECOND_PROVIDER_GRAPHIC_ELEMENTS_PARAMS } from "../graphql";
import { getGraphicElementsFromGQResult, GraphicElementEntity, GraphQLResponse } from "../processors";
import { GraphicElement as GraphicElementType } from "../processors";
import { useProviderComparePageContext, useProviderInfoPageContext } from "../Context";
import _ from "lodash";
import { EMPTY_ARRAY } from "../../Components/misc";

export interface GraphicElementProps {
    element: GraphicElementType;
    extraProps?: any;
}

export const GraphicElement: React.FC<GraphicElementProps> = ({ element, extraProps = {} }) => {
    // const { spUnit: spUnitFromContext, page, id: appId } = useApp();
    // const spUnit =
    //     processors.finaliseSpUnit({
    //         spUnitFromContext,
    //         spUnitFromExtraProps: (extraProps ?? {}).spUnit
    //     }) ?? {};

    // const spUnit = {
    //     current: undefined,
    //     parents: undefined
    // };
    // const { current, parents } = spUnit ?? {};
    const node = useRef(null);

    useEffect(() => {
        // only track no content for certain components
        if (element?.type !== "react" || element?.[element.type]?.component !== "categoryDisplay") {
            if (node.current && node.current.childNodes && !node.current.childNodes.length) {
                node.current.classList.add("no-content");
            } else if (node.current && node.current.childNodes && node.current.childNodes.length) {
                node.current.classList.remove("no-content");
            }
        }
    });

    const { Paragraph } = require("../Paragraph");
    const { ReactComponent } = require("../ReactComponent");
    const { KpiReportTemplate } = require("../KpiReportTemplate");

    const GraphicTypes = {
        paragraph: Paragraph,
        react: ReactComponent,
        kpiReportTemplate: KpiReportTemplate
    };

    const { className, type, graphQLQuery, transformationFn, visibilityFn } = element ?? {};

    let { query, options } = !bundle[graphQLQuery]
        ? {
              query: GET_GRAPHIC_ELEMENTS_PARAMS([], null)?.query,
              options: {
                  skip: true
              }
          }
        : bundle[graphQLQuery]({
              //   current,
              //   parents,
              extraProps
          });

    const { loading, data } = useQuery(query, options);

    if (loading) return <LoadingIndicator />;

    const afterData = processors[transformationFn]
        ? processors[transformationFn]({
              data,
              // current, parents,
              extraProps
          })
        : data;

    const visibility = processors[visibilityFn] ? processors[visibilityFn](afterData) : true;

    if (!visibility || !GraphicTypes[type] || !element[type]) return null;

    return (
        <div className={`col ${className ?? ""} graphic-element`} ref={node}>
            {React.createElement(GraphicTypes[type], {
                ...element[type],
                data: afterData,
                extraProps
            })}
        </div>
    );
};

export interface GraphicElementWithNidProps {
    nid: number;
}
export const GraphicElementWithNid: React.FC<GraphicElementWithNidProps> = ({ nid }) => {
    // passing null location to query at global visible
    const { provider: singleProvider } = useProviderInfoPageContext();
    const { provider: fromProvider, toProvider } = useProviderComparePageContext();

    const providers = useMemo(() => {
        return _.uniqBy(
            [fromProvider, toProvider, singleProvider].filter(d => d != null),
            d => d.adminId
        );
    }, [fromProvider, toProvider, singleProvider]);

    const { query, options } = GET_GRAPHIC_ELEMENTS_PARAMS([nid], !providers.length ? null : [providers?.[0]?.adminId]);

    const { query: secondProviderQuery, options: secondProviderOptions } = GET_SECOND_PROVIDER_GRAPHIC_ELEMENTS_PARAMS(
        [nid],
        providers?.[1]?.adminId
    );

    const { loading: firstLoading, data: firstData } = useQuery<GraphQLResponse<GraphicElementEntity[]>>(
        query,
        options
    );
    const { loading: secondLoading, data: secondData } = useQuery<GraphQLResponse<GraphicElementEntity[]>>(
        secondProviderQuery,
        secondProviderOptions
    );

    const adminIdToOne: Record<string, 1> = useMemo(() => {
        return (
            providers?.reduce((prev, current) => {
                if (current?.adminId != null) prev[current?.adminId] = 1;

                return prev;
            }, {}) ?? {}
        );
    }, [providers]);

    const { loading, data } = useMemo((): { loading: boolean; data: GraphQLResponse<GraphicElementEntity[]> } => {
        const adminIds = Object.keys(adminIdToOne);

        const loading = firstLoading || secondLoading;
        let data = [
            ...(firstData?.result ?? (EMPTY_ARRAY as GraphicElementEntity[])),
            ...(secondData?.result ?? (EMPTY_ARRAY as GraphicElementEntity[]))
        ];

        data = _.uniqBy(data, d => d.nid);
        data = data.filter(ge => {
            return (
                // global
                (ge.displayFor === "all" && (!ge?.providers || ge?.providers?.length === 0)) ||
                // has the given provider
                (ge.displayFor === "node" && ge?.providers?.find(p => adminIdToOne[p?.adminId] === 1) != null) ||
                // pair modes
                (adminIds?.length > 1 &&
                    ge.displayFor === "pair" &&
                    _.uniq(
                        ge?.providers
                            ?.map(p => p?.adminId)
                            ?.filter(d => d != null && d.trim() !== "" && adminIdToOne[d] === 1) ?? []
                    ).length === adminIds.length)
            );
        });

        return {
            loading,
            data: {
                result: data
            }
        };
    }, [firstLoading, secondLoading, firstData, secondData, adminIdToOne]);

    if (loading)
        return (
            <div className="col-12">
                <LoadingIndicator />
            </div>
        );

    const graphicElement = getGraphicElementsFromGQResult(data?.result)?.[0];
    if (!graphicElement) return null;

    return (
        <>
            <GraphicElement element={graphicElement} />
        </>
    );
};
